/**
 * Pure helpers for the SSID save-safety and Passpoint-state UI (OP#1778, OP#1776; 2026-10-12).
 *
 * Kept out of the route components so `npm run test:unit` pins the wording and the rules:
 *   - "no target" is "All APs in site", said out loud;
 *   - an "All APs" SSID that is not on every AP is flagged (its next save spreads it);
 *   - a HARD save warning (an AP left with no SSID) needs its own explicit tick;
 *   - Passpoint state is per AP: "Partial · 1 of 3 APs" instead of reading the first AP only.
 */
import type { Passpoint, Ssid, SsidImpactAp, SsidWarning } from "@/types/domain";

/** Operator wording of an SSID's target (mirrors services/ssid_manager.target_label). */
export const ALL_APS_LABEL = "All APs in site";

export function targetLabel(ssid: Pick<Ssid, "target" | "target_label">): string {
  if (ssid.target_label) return ssid.target_label;
  return ssid.target && ssid.target !== "all" ? `Group: ${ssid.target}` : ALL_APS_LABEL;
}

/** True when an enabled "All APs" SSID is on fewer APs than the site has (drift to warn on). */
export function coverageGap(ssid: Pick<Ssid, "enabled" | "target" | "coverage">): boolean {
  if (!ssid.enabled || (ssid.target ?? "all") !== "all" || !ssid.coverage) return false;
  return ssid.coverage.on < ssid.coverage.of;
}

/** The hard warnings (an AP left broadcasting nothing) among a save's warnings. */
export function hardWarnings(warnings: SsidWarning[]): SsidWarning[] {
  return warnings.filter((w) => w.severity === "hard");
}

/** Names of the APs a hard warning says will go dark (deduplicated, order kept). */
export function darkAps(warnings: SsidWarning[]): string[] {
  const out: string[] = [];
  for (const w of hardWarnings(warnings)) {
    for (const d of w.devices ?? []) if (!out.includes(d)) out.push(d);
  }
  return out;
}

/** "Save anyway" is allowed only once every hard warning has been explicitly ticked. */
export function canSaveAnyway(warnings: SsidWarning[], ackNoSsid: boolean): boolean {
  return hardWarnings(warnings).length === 0 || ackNoSsid;
}

/** "AP-1, AP-2 (offline)" for an impact list. */
export function apListLabel(aps: SsidImpactAp[]): string {
  return aps.map((a) => (a.offline ? `${a.name} (offline)` : a.name)).join(", ");
}

/** Passpoint list pill: tone + text from the per-AP state (never "off" while any AP has it). */
export function passpointPill(
  p: Pick<Passpoint, "enabled" | "state" | "enabled_count" | "device_count">,
): { tone: "healthy" | "warn" | "off"; text: string } {
  if (p.state === "partial") {
    return { tone: "warn", text: `Partial · ${p.enabled_count ?? "?"} of ${p.device_count ?? "?"} APs` };
  }
  if (p.enabled) return { tone: "healthy", text: "Passpoint" };
  return { tone: "off", text: "off" };
}
